/* Канбан: колонки этапов с горизонтальным скроллом.

   Карточки переносятся пальцем — между этапами и внутри этапа. Жест берём не
   сразу: сначала удержание, иначе первое же движение вверх воровало бы прокрутку
   страницы. Взяли карточку — под пальцем едет её копия, соседи раздвигаются,
   освобождая место под сброс.

   Главное правило: пока палец не отпущен, раскладка НЕ меняется. Браузер
   доставляет жест элементу, где началось касание; уберёшь его из DOM — touchmove
   перестаёт доходить, браузер забирает жест под прокрутку и шлёт pointercancel.
   Перекладывание карточки в другую колонку в React — это как раз unmount+mount
   (между родителями узлы не переезжают). Поэтому во время переноса поднятая
   карточка стоит в DOM на своём месте (невидимая), зазоры открываются чистыми
   CSS-трансформами соседей, а настоящая перестановка происходит один раз —
   при отпускании, когда жест уже завершён. Так работают dnd-kit и SortableJS.

   Перетаскивание включается пропом `onMove`. Без него канбан обычный, только
   листается — так работают доски аккаунтинга и разработки. */

import { rub } from "@mera/shared";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { StageBar } from "./ui.jsx";
import { haptic } from "../lib/tg.js";

/** Сколько держать палец, прежде чем карточка «отлипнет» */
const HOLD_MS = 220;
/** Дальше этого сдвига до срабатывания удержания — это прокрутка, а не перенос.
    Порог маленький: браузер решает листать примерно на пяти-восьми пикселях,
    и отдать ему жест надо раньше, чем он заберёт его сам */
const SLIP = 5;
/** Полоса у края экрана, в которой доска сама подкручивается под пальцем */
const EDGE = 56;
const EDGE_SPEED = 14;
/** Вертикальный зазор между карточками колонки (gap-2) */
const GAP = 8;

const idsOf = (items) => items.map((it) => it.id);

const boardOf = (columns) =>
  columns.reduce((acc, c) => {
    acc[c.key] = c.items;
    return acc;
  }, {});

export default function Kanban({ columns, render, onMove, sumOf }) {
  /* Раскладка в состоянии: после сброса карточки она перестраивается сразу,
     не дожидаясь сервера. Ответ сервера подхватываем, когда он реально другой. */
  const signature = useMemo(
    () => columns.map((c) => `${c.key}:${idsOf(c.items).join(",")}`).join("|"),
    [columns]
  );
  const [board, setBoard] = useState(() => boardOf(columns));
  const [drag, setDrag] = useState(null);
  /* Куда ляжет карточка: колонка и место среди остальных (без неё самой) */
  const [slot, setSlot] = useState(null);

  const boardRef = useRef(board);
  boardRef.current = board;
  const dragging = useRef(false);
  const columnsRef = useRef(columns);
  columnsRef.current = columns;

  useEffect(() => {
    if (!dragging.current) setBoard(boardOf(columnsRef.current));
  }, [signature]);

  const scroller = useRef(null);
  const ghost = useRef(null);
  const colEls = useRef(new Map());
  const cardEls = useRef(new Map());
  const grip = useRef(null);
  const point = useRef({ x: 0, y: 0 });
  const slotRef = useRef(null);
  const origin = useRef(null);
  const moved = useRef(false);

  /* ── FLIP на сбросе: перед перестановкой снимаем позиции (уже со сдвигами),
        после — проигрываем путь от них к финальным. Так карточки доезжают,
        а не телепортируются, какой бы высоты они ни были. ── */
  const places = useRef(new Map());
  const snapshot = () => {
    const was = new Map();
    cardEls.current.forEach((el, id) => { if (el) was.set(id, el.getBoundingClientRect()); });
    places.current = was;
  };

  useLayoutEffect(() => {
    const was = places.current;
    if (!was.size) return;
    places.current = new Map();
    cardEls.current.forEach((el, id) => {
      const first = was.get(id);
      if (!el || !first) return;
      const now = el.getBoundingClientRect();
      if (Math.abs(first.top - now.top) < 1 && Math.abs(first.left - now.left) < 1) return;
      el.style.transition = "none";
      el.style.transform = `translate3d(${first.left - now.left}px, ${first.top - now.top}px, 0)`;
      requestAnimationFrame(() => {
        el.style.transition = "transform 220ms cubic-bezier(.2,.8,.2,1)";
        el.style.transform = "";
      });
    });
  }, [board]);

  /* ── Куда встанет карточка: колонка под пальцем, место — по серединам соседей.
        Меряем живые прямоугольники: они уже включают текущие сдвиги. ── */
  const aim = useCallback((x, y) => {
    const dragId = grip.current?.id;
    if (dragId == null || !dragging.current) return;

    let col = null;
    colEls.current.forEach((el, key) => {
      if (col || !el) return;
      const r = el.getBoundingClientRect();
      if (x >= r.left - 8 && x <= r.right + 8) col = key;
    });
    if (!col) return;

    const others = (boardRef.current[col] || []).filter((it) => it.id !== dragId);
    let index = others.length;
    for (let i = 0; i < others.length; i += 1) {
      const r = cardEls.current.get(others[i].id)?.getBoundingClientRect();
      if (r && y < r.top + r.height / 2) { index = i; break; }
    }

    const cur = slotRef.current;
    if (cur && cur.col === col && cur.index === index) return;
    slotRef.current = { col, index };
    haptic.select();
    setSlot(slotRef.current);
  }, []);

  /* ── Подкрутка доски и страницы, пока палец у края ── */
  useEffect(() => {
    if (!drag) return undefined;
    const page = scroller.current?.closest(".page") || null;
    let frame = 0;
    let seen = null;
    const tick = () => {
      const { x, y } = point.current;
      const box = scroller.current?.getBoundingClientRect();
      if (box) {
        if (x < box.left + EDGE) scroller.current.scrollLeft -= EDGE_SPEED;
        else if (x > box.right - EDGE) scroller.current.scrollLeft += EDGE_SPEED;
      }
      if (page) {
        if (y < EDGE + 40) page.scrollTop -= EDGE_SPEED;
        else if (y > window.innerHeight - EDGE - 40) page.scrollTop += EDGE_SPEED;
      }
      // Прицеливаемся только когда что-то изменилось: палец сдвинулся или доска уехала
      const now = `${x},${y},${scroller.current?.scrollLeft || 0},${page?.scrollTop || 0}`;
      if (now !== seen) {
        seen = now;
        aim(x, y);
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [drag, aim]);

  /* ── Жест. Все слушатели вешаются синхронно в момент касания: между setState
        и эффектом React проходит кадр, и первое движение пальца успевало бы
        проскочить мимо — браузер посчитал бы жест прокруткой. ── */
  const gesture = useRef(null);
  const stopGesture = useCallback(() => { gesture.current?.off(); }, []);

  const startDrag = (item, col, x, y) => {
    const el = cardEls.current.get(item.id);
    if (!el) return;
    const r = el.getBoundingClientRect();
    const index = (boardRef.current[col] || []).findIndex((it) => it.id === item.id);
    dragging.current = true;
    // Клик, который придёт после подъёма карточки, гасим: это был перенос, а не тап
    moved.current = true;
    origin.current = { col, index };
    slotRef.current = { col, index };
    point.current = { x, y };
    grip.current = { id: item.id, dx: x - r.left, dy: y - r.top };
    haptic.medium();
    setSlot(slotRef.current);
    setDrag({ id: item.id, item, w: r.width, h: r.height, x, y });
  };

  /* Сброс: одна настоящая перестановка — жест уже завершён, DOM можно менять */
  const finishDrag = () => {
    const at = slotRef.current;
    const from = origin.current;
    const dragId = grip.current?.id;
    dragging.current = false;
    setDrag(null);
    setSlot(null);
    slotRef.current = null;
    if (!at || dragId == null) return;
    // Подняли и положили на то же место — сервер об этом знать не обязан
    if (from && from.col === at.col && at.index === from.index) return;

    let card = null;
    const next = {};
    Object.entries(boardRef.current).forEach(([key, items]) => {
      next[key] = items.filter((it) => (it.id === dragId ? ((card = it), false) : true));
    });
    if (!card) return;
    next[at.col] = [...next[at.col].slice(0, at.index), card, ...next[at.col].slice(at.index)];

    haptic.light();
    snapshot();
    boardRef.current = next;
    setBoard(next);
    onMove(at.col, idsOf(next[at.col]));
  };

  const onPointerDown = (e, item, col) => {
    if (!onMove) return;
    if (e.pointerType === "mouse" && e.button !== 0) return;
    stopGesture();
    // Новый палец — новый жест: прошлый перенос не должен съесть будущий тап
    moved.current = false;

    const x0 = e.clientX;
    const y0 = e.clientY;

    const onTouchMove = (ev) => { if (dragging.current) ev.preventDefault(); };
    // Длинное удержание на Android открывает системное меню — оно рвёт жест
    const onCtx = (ev) => ev.preventDefault();
    const onMoveEvt = (ev) => {
      if (!dragging.current) {
        // Двинулись раньше, чем карточка отлипла — это прокрутка, жест отдаём браузеру
        if (Math.abs(ev.clientX - x0) > SLIP || Math.abs(ev.clientY - y0) > SLIP) stopGesture();
        return;
      }
      moved.current = true;
      point.current = { x: ev.clientX, y: ev.clientY };
      const g = grip.current;
      if (ghost.current && g) {
        ghost.current.style.transform =
          `translate3d(${ev.clientX - g.dx}px, ${ev.clientY - g.dy}px, 0) scale(1.04)`;
      }
    };
    const onUp = () => {
      if (dragging.current) finishDrag();
      stopGesture();
    };

    document.addEventListener("touchmove", onTouchMove, { passive: false });
    document.addEventListener("contextmenu", onCtx);
    window.addEventListener("pointermove", onMoveEvt);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    const timer = setTimeout(() => startDrag(item, col, x0, y0), HOLD_MS);

    gesture.current = {
      off: () => {
        clearTimeout(timer);
        document.removeEventListener("touchmove", onTouchMove);
        document.removeEventListener("contextmenu", onCtx);
        window.removeEventListener("pointermove", onMoveEvt);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onUp);
        gesture.current = null;
      },
    };
  };

  useEffect(() => stopGesture, [stopGesture]);

  /* Сдвиг соседа на время переноса: закрыть дыру в исходной колонке,
     открыть — в целевой. Сам сдвиг — чистый transform, раскладка не трогается. */
  const shiftOf = (colKey, idx, id) => {
    if (!drag || !slot || id === drag.id) return 0;
    const H = drag.h + GAP;
    const from = origin.current;
    // Место карточки в списке без поднятой — по нему сверяемся с целевым зазором
    const pos = colKey === from.col && idx > from.index ? idx - 1 : idx;
    let shift = 0;
    if (colKey === from.col && idx > from.index) shift -= H;
    if (colKey === slot.col && pos >= slot.index) shift += H;
    return shift;
  };

  return (
    <>
      <div
        ref={scroller}
        className="hscroll flex gap-3 px-4 pt-3 items-start"
        style={drag ? { touchAction: "none", scrollSnapType: "none" } : undefined}
      >
        {columns.map((c) => {
          const items = board[c.key] || [];
          const sum = sumOf ? items.reduce((s, it) => s + (sumOf(it) || 0), 0) : null;
          return (
            <div
              key={c.key}
              ref={(el) => { if (el) colEls.current.set(c.key, el); else colEls.current.delete(c.key); }}
              className="w-[248px] shrink-0"
              style={{ opacity: c.muted ? 0.6 : 1, scrollSnapAlign: "start" }}
            >
              {/* Заголовок: название, число карточек в скобках и деньги колонки справа */}
              <div className="px-0.5">
                <div className="flex items-baseline gap-1.5">
                  <span
                    className="text-[13px] font-semibold truncate"
                    style={{ color: c.muted ? "var(--tg-hint)" : "var(--tg-text)" }}
                  >
                    {c.title}
                  </span>
                  <span className="num text-[12px] font-semibold shrink-0" style={{ color: "var(--tg-hint)" }}>
                    ({items.length})
                  </span>
                  {sum != null ? (
                    <span className="num ms-auto text-[12px] font-semibold shrink-0">{rub(sum)}</span>
                  ) : null}
                </div>
                <StageBar color={c.color} />
              </div>

              <div className="mt-2.5 flex flex-col gap-2">
                {items.length ? (
                  items.map((it, idx) => {
                    const lifted = drag?.id === it.id;
                    const shift = drag ? shiftOf(c.key, idx, it.id) : 0;
                    return (
                      <div
                        key={it.id}
                        ref={(el) => { if (el) cardEls.current.set(it.id, el); else cardEls.current.delete(it.id); }}
                        onPointerDown={(e) => onPointerDown(e, it, c.key)}
                        /* Перенос уже был — гасим клик, который браузер шлёт следом */
                        onClickCapture={(e) => {
                          if (!moved.current) return;
                          e.preventDefault();
                          e.stopPropagation();
                          moved.current = false;
                        }}
                        style={{
                          WebkitUserSelect: "none",
                          userSelect: "none",
                          WebkitTouchCallout: "none",
                          // Поднятая стоит в DOM невидимой — см. комментарий сверху
                          opacity: lifted ? 0 : undefined,
                          transform: shift ? `translate3d(0, ${shift}px, 0)` : undefined,
                          transition: drag ? "transform 200ms cubic-bezier(.2,.8,.2,1)" : undefined,
                        }}
                      >
                        {render(it)}
                      </div>
                    );
                  })
                ) : (
                  <div
                    className="rounded-xl border border-dashed py-5 text-center text-[12px]"
                    style={{ borderColor: "var(--tg-separator)", color: "var(--tg-hint)" }}
                  >
                    пусто
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Копия под пальцем: приподнята тенью и чуть увеличена — видно, что её несут */}
      {drag ? (
        <div
          ref={ghost}
          className="fixed left-0 top-0 z-50 pointer-events-none"
          style={{
            width: drag.w,
            transform: `translate3d(${drag.x - grip.current.dx}px, ${drag.y - grip.current.dy}px, 0) scale(1.04)`,
            filter: "drop-shadow(0 12px 24px rgba(0,0,0,0.28))",
          }}
        >
          {render(drag.item)}
        </div>
      ) : null}
    </>
  );
}
